import React, { useState } from 'react';
import { Upload, FileText, CheckCircle, AlertCircle, Download, X } from 'lucide-react';
import { SUPABASE_URL, SUPABASE_ANON_KEY } from '../../config/env.js';

const LeadsCSVUpload = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ total: 0, processed: 0, success: 0, failed: 0 });
  const [previewData, setPreviewData] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [errorDetails, setErrorDetails] = useState([]);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [uploadHistory, setUploadHistory] = useState([]);

  const parseCSV = (csvText) => {
    const sanitize = (val) => {
      const v = (val || '').trim();
      return v.replace(/^"+|"+$/g, '').trim();
    };

    const parseLine = (line) => {
      const out = [];
      let cur = '';
      let inQuotes = false;
      for (let i = 0; i < line.length; i++) {
        const ch = line[i];
        if (ch === '"') {
          if (inQuotes && line[i + 1] === '"') {
            cur += '"';
            i++;
          } else {
            inQuotes = !inQuotes;
          }
        } else if (ch === ',' && !inQuotes) {
          out.push(cur);
          cur = '';
        } else {
          cur += ch;
        }
      }
      out.push(cur);
      return out;
    };

    const lines = csvText.replace(/^\uFEFF/, '').split(/\r?\n/).filter(l => l.length > 0);
    const headers = parseLine(lines[0]).map(h => sanitize(h).toLowerCase());
    const leads = [];

    const requiredColumns = ['name', 'contact_number', 'address', 'car_number', 'car_model', 'pin_code', 'state'];
    const missingColumns = requiredColumns.filter(col => !headers.includes(col));
    if (missingColumns.length > 0) {
      throw new Error(`Missing required columns: ${missingColumns.join(', ')}`);
    }

    for (let i = 1; i < lines.length; i++) {
      const raw = lines[i];
      if (!raw || !raw.trim()) continue;
      const values = parseLine(raw).map(sanitize);
      const get = (key) => values[headers.indexOf(key)] || '';
      const lead = {
        name: get('name'),
        email: get('email'),
        contact_number: get('contact_number'),
        address: get('address'),
        car_number: get('car_number'),
        car_model: get('car_model'),
        pin_code: get('pin_code'),
        state: get('state'),
        car_purchase_date: get('car_purchase_date') || null,
        engine_number: get('engine_number') || null,
        chessis_number: get('chessis_number') || null,
        fuel_type: get('fuel_type') || null,
        status: 'new',
      };
      leads.push(lead);
    }

    return leads;
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      alert('Please select a CSV file');
      return;
    }

    setSelectedFile(file);
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const csvText = event.target.result;
        const leads = parseCSV(csvText);
        setPreviewData(leads.slice(0, 5));
        setShowPreview(true);
      } catch (error) {
        alert(`Error parsing CSV: ${error.message}`);
        setSelectedFile(null);
      }
    };
    reader.readAsText(file);
  };

  const validateLead = (lead) => {
    if (!lead.name) return 'Missing name';
    if (!lead.contact_number) return 'Missing contact number';
    const digits = (lead.contact_number || '').replace(/\D/g, '');
    if (digits.length !== 10) return 'Contact number must be 10 digits';
    if (!lead.address) return 'Missing address';
    if (!lead.car_number) return 'Missing car number';
    if (!lead.car_model) return 'Missing car model';
    if (!lead.pin_code) return 'Missing pin code';
    if (!lead.state) return 'Missing state';
    return null;
  };

  const uploadLeads = async (leads) => {
    setIsUploading(true);
    setUploadProgress({ total: leads.length, processed: 0, success: 0, failed: 0 });
    setErrorDetails([]);

    try {
      let successCount = 0;
      let failedCount = 0;

      for (let i = 0; i < leads.length; i++) {
        const lead = leads[i];
        try {
          const validationError = validateLead(lead);
          if (validationError) {
            failedCount++;
            setErrorDetails(prev => [...prev, {
              index: i + 1,
              name: lead.name,
              contact: lead.contact_number,
              reason: validationError,
            }]);
            setUploadProgress(prev => ({
              ...prev,
              processed: prev.processed + 1,
              success: successCount,
              failed: failedCount,
            }));
            continue;
          }

          const response = await fetch(`${SUPABASE_URL}/rest/v1/rpc/admin_bulk_upsert_leads`, {
            method: 'POST',
            headers: {
              'apikey': SUPABASE_ANON_KEY,
              'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              p_leads_data: [{
                name: lead.name,
                email: lead.email || null,
                contact_number: lead.contact_number,
                vehicle_model: lead.car_model || null,
                vehicle_number: lead.car_number || null,
                car_purchase_date: lead.car_purchase_date || null,
                engine_number: lead.engine_number || null,
                chessis_number: lead.chessis_number || null,
                fuel_type: lead.fuel_type || null
              }]
            }),
          });

          const result = await response.json();
          if (response.ok && result && result.length > 0 && result[0].success) {
            successCount++;
          } else {
            failedCount++;
            setErrorDetails(prev => [...prev, {
              index: i + 1,
              name: lead.name,
              contact: lead.contact_number,
              reason: result?.[0]?.message || 'Upload failed',
            }]);
          }
        } catch (error) {
          failedCount++;
          setErrorDetails(prev => [...prev, {
            index: i + 1,
            name: lead.name,
            contact: lead.contact_number,
            reason: error.message || 'Network error',
          }]);
        }

        setUploadProgress(prev => ({
          ...prev,
          processed: prev.processed + 1,
          success: successCount,
          failed: failedCount,
        }));
      }

      const uploadRecord = {
        id: Date.now(),
        filename: selectedFile?.name || 'Unknown',
        totalLeads: leads.length,
        successCount,
        failedCount,
        timestamp: new Date().toISOString()
      };
      setUploadHistory(prev => [uploadRecord, ...prev]);

      if (failedCount > 0) {
        alert(`Upload Complete\n\nSuccessfully uploaded: ${successCount}\nFailed: ${failedCount}`);
        setShowErrorModal(true);
      } else {
        alert(`Successfully uploaded ${successCount} leads!`);
      }

      setSelectedFile(null);
      setPreviewData([]);
      setShowPreview(false);
    } catch (error) {
      alert('Failed to upload leads: ' + error.message);
    } finally {
      setIsUploading(false);
    }
  };

  const downloadTemplate = () => {
    const template = `name,contact_number,address,car_number,car_model,pin_code,state,car_purchase_date,engine_number,chessis_number,fuel_type
John Doe,9876543210,123 Main St,DL01AB1234,Honda City,110001,Delhi,2020-01-15,ENG123456,CHS789012,Petrol`;
    
    const blob = new Blob([template], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'leads_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Leads CSV Upload</h1>
        <p className="text-gray-600 mt-1">Upload leads for telecallers via CSV file</p>
      </div>

      {/* Template Download */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-gray-900 mb-1">CSV Template</h3>
            <p className="text-sm text-gray-600">Download the template to see the required format</p>
          </div>
          <button
            onClick={downloadTemplate}
            className="flex items-center space-x-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            <Download size={18} />
            <span>Download Template</span>
          </button>
        </div>
      </div>

      {/* File Upload */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
        <h3 className="font-semibold text-gray-900 mb-4">Upload CSV File</h3>
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
          <input
            type="file"
            accept=".csv"
            onChange={handleFileSelect}
            disabled={isUploading}
            className="hidden"
            id="csv-upload"
          />
          <label
            htmlFor="csv-upload"
            className={`cursor-pointer flex flex-col items-center ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <Upload size={48} className="text-gray-400 mb-4" />
            <p className="text-gray-600 mb-2">
              {selectedFile ? selectedFile.name : 'Click to select CSV file'}
            </p>
            <p className="text-sm text-gray-500">CSV files only</p>
          </label>
        </div>

        {showPreview && previewData.length > 0 && (
          <div className="mt-6">
            <h4 className="font-semibold text-gray-900 mb-3">Preview (First 5 records)</h4>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left">Name</th>
                    <th className="px-4 py-2 text-left">Contact</th>
                    <th className="px-4 py-2 text-left">Car Model</th>
                    <th className="px-4 py-2 text-left">State</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {previewData.map((lead, idx) => (
                    <tr key={idx}>
                      <td className="px-4 py-2">{lead.name}</td>
                      <td className="px-4 py-2">{lead.contact_number}</td>
                      <td className="px-4 py-2">{lead.car_model}</td>
                      <td className="px-4 py-2">{lead.state}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-4 flex gap-3">
              <button
                onClick={() => {
                  const reader = new FileReader();
                  reader.onload = (e) => {
                    const leads = parseCSV(e.target.result);
                    uploadLeads(leads);
                  };
                  reader.readAsText(selectedFile);
                }}
                disabled={isUploading}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                Upload All Leads
              </button>
              <button
                onClick={() => {
                  setSelectedFile(null);
                  setPreviewData([]);
                  setShowPreview(false);
                }}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Upload Progress */}
      {isUploading && (
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <h3 className="font-semibold text-gray-900 mb-4">Upload Progress</h3>
          <div className="space-y-3">
            <div className="flex justify-between text-sm">
              <span>Processed: {uploadProgress.processed} / {uploadProgress.total}</span>
              <span className="text-green-600">Success: {uploadProgress.success}</span>
              <span className="text-red-600">Failed: {uploadProgress.failed}</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-indigo-600 h-2 rounded-full transition-all"
                style={{ width: `${(uploadProgress.processed / uploadProgress.total) * 100}%` }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Error Modal */}
      {showErrorModal && errorDetails.length > 0 && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900">Upload Errors</h3>
              <button onClick={() => setShowErrorModal(false)}>
                <X size={24} className="text-gray-500" />
              </button>
            </div>
            <div className="space-y-2">
              {errorDetails.map((error, idx) => (
                <div key={idx} className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm font-medium text-red-900">
                    Row {error.index}: {error.name} ({error.contact})
                  </p>
                  <p className="text-xs text-red-700 mt-1">{error.reason}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Upload History */}
      {uploadHistory.length > 0 && (
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <h3 className="font-semibold text-gray-900 mb-4">Upload History</h3>
          <div className="space-y-3">
            {uploadHistory.map((record) => (
              <div key={record.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium text-gray-900">{record.filename}</p>
                  <p className="text-sm text-gray-600">
                    {new Date(record.timestamp).toLocaleString()}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-900">
                    Total: {record.totalLeads} | 
                    <span className="text-green-600 ml-2">Success: {record.successCount}</span>
                    {record.failedCount > 0 && (
                      <span className="text-red-600 ml-2">Failed: {record.failedCount}</span>
                    )}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadsCSVUpload;

